import { hasMarketingConsent, hasAnalyticsConsent } from './consent-state'
import { resolveProductHost } from '../product-hosts'
import { readPurchaseAttribution } from './purchase-attribution'
// Conversion attribution capture for the user_created S2S funnel.
//
// Collects browser-side ad-click identifiers at conversion time so the backend
// can link the activation to the originating ad (Meta fbc/fbp, Google gclid,
// Gravity getCAPIData). Every field is best-effort: any source that is missing
// or throws is simply omitted, and the function itself never throws — it must
// never block the auth flow it rides on.

import {
  captureCurrentCampaignContext,
  getCampaignEventProperties,
  prepareAnonymousAnalyticsContext,
  readAnalyticsCookie,
} from './track'
import { getReferralCode } from '@/lib/referral'
import { getPartnerId } from '@/lib/partner'

declare global {
  interface Window {
    // Installed by gr-pix.js (loaded async by the Gravity base snippet, distinct
    // from the `window.gravity` command queue). Exposes getCAPIData(), which
    // returns the browser attribution blob (user_data / client_context /
    // event_source_url) for server-side CAPI forwarding, and track(), the
    // tracker's only event entry point (the queue handles just "init").
    gravityPixel?: {
      getCAPIData?: () => unknown
      track?: (eventType: string, metadata?: Record<string, unknown>) => void
    }
  }
}

// First-touch UTM keys persisted to users.initial_attribution. Kept in sync
// with the backend whitelist (apps/api/schemas/auth_self.py::_UTM_KEYS).
const ACQUISITION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'ttclid',
  'landing_page',
  'referrer',
  'referring_domain',
  'acquisition_source',
  'acquisition_medium',
  'acquisition_channel',
  'anonymous_id',
] as const

export type ConversionAttribution = {
  marketing_consent?: boolean
  analytics_consent?: boolean
  fbc?: string
  fbp?: string
  gclid?: string
  gbraid?: string
  wbraid?: string
  event_source_url?: string
  gravity?: Record<string, unknown>
  // First-touch UTM params, sent so the backend can persist per-user
  // attribution at signup. Omitted when the visit carried no UTM.
  utm?: Record<string, string>
  // Referral code from the soku_ref cookie (?ref= landing param); the backend
  // binds it to a referral at account creation. Omitted when absent.
  referral_code?: string
  // Co-branding partner id from the soku_partner cookie, set when a visitor
  // starts signing up on a partner's page. The backend re-resolves it against a
  // live partner before writing — it lands on a foreign key and this value is
  // visitor-controlled, so nothing downstream trusts it as-is.
  partner_id?: string
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined
}

/**
 * Best-effort snapshot of ad-click attribution for the current page. Safe to
 * call at any time; returns an empty object on the server or when nothing is
 * available. Never throws.
 */
export function getConversionAttribution(): ConversionAttribution {
  if (typeof window === 'undefined') {
    return {}
  }
  try {
    prepareAnonymousAnalyticsContext()
  } catch {
    // Acquisition enrichment must never block authentication.
  }
  const attribution: ConversionAttribution = {}

  // Meta pixel cookies (the BFF strips request cookies, so we read + forward
  // them in the body instead).
  const fbc = readAnalyticsCookie('_fbc')
  if (fbc) attribution.fbc = fbc
  const fbp = readAnalyticsCookie('_fbp')
  if (fbp) attribution.fbp = fbp

  let campaign: ReturnType<typeof getCampaignEventProperties> = {}
  try {
    campaign = getCampaignEventProperties()
  } catch {
    campaign = {}
  }

  // Google click ids: stored latest/first touch, then current URL.
  try {
    const current = new URLSearchParams(window.location.search)
    const keys = ['gclid', 'gbraid', 'wbraid'] as const
    let selected: { key: (typeof keys)[number]; value: string } | null = null
    const touches = resolveProductHost(window.location.host).product === 'toolfare'
      ? ['first_touch', 'latest_touch'] as const : ['latest_touch', 'first_touch'] as const
    for (const prefix of touches) {
      for (const key of keys) {
        const value = asString(campaign[`${prefix}_${key}`])
        if (value) {
          selected = { key, value }
          break
        }
      }
      if (selected) break
    }
    if (!selected) {
      for (const key of keys) {
        const value = asString(current.get(key))
        if (value) {
          selected = { key, value }
          break
        }
      }
    }
    if (selected) attribution[selected.key] = selected.value
  } catch {
    // ignore — click identifiers are optional
  }

  // event_source_url: the captured (already-sanitized) landing page, else the
  // current page's origin+path only. The query is dropped on purpose — the auth
  // page URL can carry a nested invite/reset token (e.g.
  // ?redirect_url=/accept-invitation?token=...) that must never reach the ad
  // platforms. Meta needs this for action_source="website".
  try {
    const landing = asString(campaign.first_touch_landing_page)
    attribution.event_source_url =
      landing ?? `${window.location.origin}${window.location.pathname}`
  } catch {
    // ignore — event_source_url is optional
  }

  // First-touch UTM (from stored campaign context). Persisted per-user at
  // signup for channel attribution. First-touch, not latest, so the original
  // acquisition source is what sticks.
  try {
    const utm: Record<string, string> = {}
    for (const key of ACQUISITION_KEYS) {
      const value = asString(campaign[`first_touch_${key}`])
      if (value) utm[key] = value
    }
    if (Object.keys(utm).length > 0) attribution.utm = utm
  } catch {
    // ignore — utm is optional
  }

  // Referral code (invite-a-friend) — cookie-stored, validated on read.
  try {
    const referralCode = getReferralCode()
    if (referralCode) attribution.referral_code = referralCode
  } catch {
    // ignore — referral is optional
  }

  // Co-branding partner — cookie-stored, validated on read.
  try {
    const partnerId = getPartnerId()
    if (partnerId) attribution.partner_id = partnerId
  } catch {
    // ignore — partner attribution is optional
  }

  // Gravity attribution blob (click_id / visitor_id / client_context). The
  // tracker loads async; getCAPIData may be absent or return null pre-load.
  try {
    const getter = window.gravityPixel?.getCAPIData
    if (typeof getter === 'function') {
      const blob = getter()
      if (blob && typeof blob === 'object') {
        // JSON round-trip so a cyclic / BigInt payload can't make the later
        // JSON.stringify(body) throw and block the auth submit.
        attribution.gravity = JSON.parse(JSON.stringify(blob)) as Record<
          string,
          unknown
        >
      }
    }
  } catch {
    // ignore — Gravity pixel may not be loaded or returned non-serializable data
  }

  // Connect on toolfare requires explicit consent; preserve legacy Soku policy.
  if (resolveProductHost(window.location.host).product === 'toolfare') {
    attribution.marketing_consent = hasMarketingConsent()
    attribution.analytics_consent = hasAnalyticsConsent()
    if (!attribution.marketing_consent) {
      for (const key of ['fbc', 'fbp', 'gclid', 'gbraid', 'wbraid', 'gravity'] as const) {
        delete attribution[key]
      }
    }
  }
  if (attribution.marketing_consent === true &&
      resolveProductHost(window.location.host).product === 'toolfare') {
    const click = asString(campaign.first_touch_fbclid)
    const seen = Date.parse(asString(campaign.first_touch_seen_at) ?? '')
    if (click && Number.isFinite(seen)) attribution.fbc = `fb.1.${seen}.${click}`
  }
  return attribution
}

/** Checkout attribution is separate from immutable signup acquisition. */
export function getPurchaseAttribution() {
  try {
    captureCurrentCampaignContext()
    return readPurchaseAttribution(readAnalyticsCookie)
  } catch {
    return { marketing_consent: false }
  }
}
